import { AuthService } from '@teko/auth';
import { UserService } from '@teko/user';
import { DiscoveryService } from '@teko/discovery';
import { CartService } from '@teko/cart';
import { CartService as CartServiceV2 } from '@teko/cart/v2';
import { LocationService } from '@teko/location';
import { NotificationService } from '@teko/notification';
import { WarehouseService } from '@teko/warehouse';
import type { TenantEnv } from 'types';
import {
  OrderService,
  PaymentServiceWrapper as PaymentService,
  PaymentBffServiceWrapper as PaymentBffService,
  QuotationService,
  TerraService,
  LoyaltyServiceWrapper as LoyaltyService,
  LoyaltyBFFServiceWrapper as LoyaltyBFFService,
} from '../lazy-modules';
import { IOrderService } from '@teko/order';
import { IPaymentService, IPaymentBffService } from '@teko/payment';
import { IQuotationService } from 'services/quotation';
import { ITerraService } from '@teko/terra-bff';
import { ILoyaltyService, ILoyaltyBFFService } from '@teko/loyalty';
import { isServerless } from '../helpers';

export class ServiceContainer {
  private _authService: AuthService;
  private _userService: UserService;
  private _discoveryService: DiscoveryService;
  private _discoveryPos365Service: DiscoveryService;
  private _cartService: CartService;
  private _cartServiceV2: CartServiceV2;
  private _locationService: LocationService;
  private _orderService: IOrderService;
  private _notificationService: NotificationService;
  private _warehouseService: WarehouseService;
  private _terraService: ITerraService;
  private _paymentService: IPaymentService;
  private _paymentBffService: IPaymentBffService;
  private _loyaltyService: ILoyaltyService;
  private _loyaltyBFFService: ILoyaltyBFFService;
  private _publicQuotationOrderService: IQuotationService;

  private tenantConfig: TenantEnv;
  constructor(tenantConfig: TenantEnv) {
    this.tenantConfig = tenantConfig;
  }

  get authService() {
    if (!this._authService) {
      this._authService = new AuthService(
        this.tenantConfig.getProcessEnv('IAM_API_URL'),
        this.tenantConfig.iamClientId ||
          this.tenantConfig.getProcessEnv('NEXT_PUBLIC_IAM_CLIENT_ID'),
        this.tenantConfig.iamOauthDomain ||
          this.tenantConfig.getProcessEnv('NEXT_PUBLIC_IAM_OAUTH_DOMAIN'),
      );
    }
    return this._authService;
  }

  get userService() {
    if (!this._userService) {
      this._userService = new UserService(
        this.tenantConfig.getProcessEnv('NEXT_PUBLIC_BASE_USER_SERVICE_URL'),
        undefined,
        this.tenantConfig.platformId,
      );
    }
    return this._userService;
  }

  get discoveryService() {
    if (!this._discoveryService) {
      const publicDiscoveryEndpoint =
        this.tenantConfig.getProcessEnv('BASE_DISCOVERY_URL');
      const internalDiscoveryEndpoint = this.tenantConfig.getProcessEnv(
        'INTERNAL_DISCOVERY_URL',
      );
      // use internal discovery endpoint
      // for server side rendering
      this._discoveryService = new DiscoveryService(
        typeof window === 'undefined' && internalDiscoveryEndpoint
          ? internalDiscoveryEndpoint
          : publicDiscoveryEndpoint,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._discoveryService;
  }

  get discoveryPos365Service() {
    if (!this._discoveryPos365Service) {
      this._discoveryPos365Service = new DiscoveryService(
        this.tenantConfig.getProcessEnv('BASE_POS365_DISCOVERY_URL'),
        this.tenantConfig.currentLanguage,
      );
    }
    return this._discoveryPos365Service;
  }

  get cartService() {
    if (!this._cartService) {
      this._cartService = new CartService(
        this.tenantConfig.getProcessEnv('BASE_CART_API_URL'),
        undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._cartService;
  }

  get cartServiceV2() {
    if (!this._cartServiceV2) {
      const endpoint = isServerless()
        ? this.tenantConfig.getProcessEnv('BASE_CART_API_URL')
        : this.tenantConfig.getProcessEnv('INTERNAL_CART_API_URL');
      this._cartServiceV2 = new CartServiceV2(endpoint, {
        language: this.tenantConfig.currentLanguage,
        location: this.tenantConfig.location,
        terminal: this.tenantConfig.terminal,
        // terminalId: this.tenantConfig.terminalId,
      });
    }
    return this._cartServiceV2;
  }

  get locationService() {
    if (!this._locationService) {
      this._locationService = new LocationService(
        this.tenantConfig.getProcessEnv('BASE_LOCATION_URL'),
      );
    }
    return this._locationService;
  }

  get orderService(): IOrderService {
    if (!this._orderService) {
      this._orderService = new OrderService(
        this.tenantConfig.getProcessEnv('BASE_COV_URL'),
      );
    }
    return this._orderService;
  }

  get notificationService() {
    if (!this._notificationService) {
      this._notificationService = new NotificationService(
        this.tenantConfig.getProcessEnv('BASE_NOTIFICATIONS_API'),
      );
    }
    return this._notificationService;
  }

  get warehouseService() {
    if (!this._warehouseService) {
      this._warehouseService = new WarehouseService(
        this.tenantConfig.getProcessEnv('BASE_WAREHOUSE_CONSUMER_API_URL'),
      );
    }
    return this._warehouseService;
  }

  get terraService(): ITerraService {
    if (!this._terraService) {
      this._terraService = new TerraService(
        this.tenantConfig.getProcessEnv('BASE_TERRA_BFF_API_URL'),
      );
    }
    return this._terraService;
  }

  get paymentService(): IPaymentService {
    if (!this._paymentService) {
      this._paymentService = new PaymentService({
        baseUrl: this.tenantConfig.getProcessEnv('BASE_PAYMENT_API_URL'),
        secretKey:
          this.tenantConfig.paymentSecretKey ||
          this.tenantConfig.getProcessEnv('PAYMENT_SECRET_KEY'),
        paymentSettingUrl: this.tenantConfig.getProcessEnv(
          'BASE_MAIN_PAYMENT_API_URL',
        ),
      });
    }
    return this._paymentService;
  }

  get paymentBffService(): IPaymentBffService {
    if (!this._paymentBffService) {
      this._paymentBffService = new PaymentBffService(
        this.tenantConfig.getProcessEnv('BASE_PAYMENT_BFF_API_URL'),
      );
    }
    return this._paymentBffService;
  }

  get loyaltyService(): ILoyaltyService {
    if (!this._loyaltyService) {
      const publicLoyaltyEndpoint = this.tenantConfig.getProcessEnv(
        'BASE_LOYALTY_API_URL',
      );
      const internalLoyaltyEndpoint = this.tenantConfig.getProcessEnv(
        'INTERNAL_LOYALTY_API_URL',
      );
      this._loyaltyService = new LoyaltyService(
        typeof window === 'undefined' && internalLoyaltyEndpoint
          ? internalLoyaltyEndpoint
          : publicLoyaltyEndpoint,
        this.tenantConfig.getProcessEnv('LOYALTY_CLIENT_ID'),
      );
    }
    return this._loyaltyService;
  }

  get loyaltyBFFService(): ILoyaltyBFFService {
    if (!this._loyaltyBFFService) {
      this._loyaltyBFFService = new LoyaltyBFFService(
        this.tenantConfig.getProcessEnv('BASE_LOYALTY_BFF_API_URL'),
        this.tenantConfig.loyaltyClientId ||
          this.tenantConfig.getProcessEnv('LOYALTY_CLIENT_ID'),
      );
    }
    return this._loyaltyBFFService;
  }

  get publicQuotataionOrderService(): IQuotationService {
    if (!this._publicQuotationOrderService) {
      this._publicQuotationOrderService = new QuotationService();
    }
    return this._publicQuotationOrderService;
  }
}
